// Pantalla a la que Wompi devuelve después de pagar el plan
// (/suscripcion/resultado?id=<transacción>). Pregunta el estado al backend,
// que en esa misma consulta activa el plan si el pago quedó aprobado (no
// depende de que ya haya llegado el aviso del servicio central de pagos).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCheckCircle, FaClock, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { consultarPagoPlan } from '../services/apiService';

// PSE y Nequi pueden tardar en confirmar: se vuelve a preguntar cada pocos
// segundos durante un rato antes de decir "sigue en proceso".
const ESPERA_MS = 4000;
const MAX_CONSULTAS = 10;

const PLANES = { BASICO: 'Básico', PRO: 'Pro' };
const PERIODOS = { MENSUAL: '1 mes', SEMESTRAL: '6 meses', VITALICIO: 'de por vida' };
const fecha = (iso) => new Date(iso).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });

export default function SuscripcionResultadoPage({ esperaMs = ESPERA_MS }) {
  const wompiId = new URLSearchParams(window.location.search).get('id');
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');
  const [consultando, setConsultando] = useState(Boolean(wompiId));
  const consultas = useRef(0);
  const temporizador = useRef(null);

  const consultar = useCallback(async () => {
    setConsultando(true);
    try {
      const datos = await consultarPagoPlan(wompiId);
      setResultado(datos);
      setError('');
      consultas.current += 1;
      if (datos.pago.estado === 'PENDIENTE' && consultas.current < MAX_CONSULTAS) {
        temporizador.current = setTimeout(consultar, esperaMs);
        return;
      }
    } catch (e) {
      setError(e.message || 'No pudimos consultar tu pago.');
    }
    setConsultando(false);
  }, [wompiId, esperaMs]);

  useEffect(() => {
    if (wompiId) consultar();
    return () => clearTimeout(temporizador.current);
  }, [wompiId, consultar]);

  const reintentar = () => {
    consultas.current = 0;
    consultar();
  };

  let contenido;
  if (!wompiId) {
    contenido = (
      <Estado icono={<FaTimesCircle />} color="text-gray-400" titulo="No encontramos el pago">
        Esta página se abre sola al volver de Wompi. Si ya pagaste, revisa tu plan en el panel.
      </Estado>
    );
  } else if (error) {
    contenido = (
      <Estado icono={<FaTimesCircle />} color="text-red-500" titulo="No pudimos consultar tu pago">
        {error}
        <Boton onClick={reintentar}>Consultar de nuevo</Boton>
      </Estado>
    );
  } else if (!resultado) {
    contenido = (
      <Estado icono={<FaSpinner className="animate-spin" />} color="text-emerald-500" titulo="Consultando tu pago…" />
    );
  } else {
    const { pago, planExpiresAt } = resultado;
    const nombre = `${PLANES[pago.plan] || pago.plan} (${PERIODOS[pago.periodo] || pago.periodo})`;
    if (pago.estado === 'APROBADO') {
      contenido = (
        <Estado icono={<FaCheckCircle />} color="text-emerald-500" titulo="¡Pago aprobado! Tu plan está activo">
          Plan {nombre}. {planExpiresAt ? `Vence el ${fecha(planExpiresAt)}.` : 'No vence.'}
          {/* Recarga completa a propósito: el aviso de solo lectura y el
              estado del plan se leen al cargar el panel. */}
          <a href="/dashboard" className="mt-6 inline-block px-6 py-3 rounded-2xl font-black text-white bg-emerald-500 hover:bg-emerald-600">
            Ir al panel
          </a>
        </Estado>
      );
    } else if (pago.estado === 'PENDIENTE') {
      contenido = (
        <Estado icono={consultando ? <FaSpinner className="animate-spin" /> : <FaClock />} color="text-amber-500" titulo="Tu pago está en proceso">
          Wompi todavía no lo confirma (con PSE o Nequi puede tardar unos minutos). Cuando se apruebe,
          tu plan {nombre} se activa solo, aunque cierres esta página.
          {!consultando && <Boton onClick={reintentar}>Consultar de nuevo</Boton>}
        </Estado>
      );
    } else {
      contenido = (
        <Estado icono={<FaTimesCircle />} color="text-red-500" titulo="El pago no se aprobó">
          No se hizo ningún cobro por el plan {nombre}. Puedes intentarlo de nuevo con otro medio de pago.
          <Link to="/planes" className="mt-6 inline-block px-6 py-3 rounded-2xl font-black text-white bg-emerald-500 hover:bg-emerald-600">
            Volver a intentar
          </Link>
        </Estado>
      );
    }
  }

  return <div className="max-w-lg mx-auto py-10 px-4">{contenido}</div>;
}

function Estado({ icono, color, titulo, children }) {
  return (
    <div className="bg-white rounded-[2rem] border border-gray-100 shadow-sm p-8 text-center">
      <div className={`text-5xl mx-auto mb-4 flex justify-center ${color}`}>{icono}</div>
      <h1 className="text-2xl font-black text-gray-900 mb-3">{titulo}</h1>
      {children && <div className="text-gray-500 text-sm leading-relaxed flex flex-col items-center">{children}</div>}
    </div>
  );
}

function Boton({ onClick, children }) {
  return (
    <button onClick={onClick} className="mt-6 px-6 py-3 rounded-2xl font-black text-emerald-700 bg-emerald-50 hover:bg-emerald-100">
      {children}
    </button>
  );
}
